import { DashboardState, ECardinality, EProjectType, IActionParameter } from "@kuika/kuika-cl-model";
import { IActionDefinitionDto } from "@kuika/kuika-cl-model/src/dtos/action.dto";
import { EDatasourceType } from "@kuika/kuika-cl-model/src/enums/datasource-type";
import { EAppSupportType } from "@kuika/kuika-cl-model/src/models/app-support-type";
import { isOfflineCompatibleActionDefinition } from "../offline-action-compatibility";

/**
 * Minimal shape for an action definition the LLM could attach to an event or
 * event-like property slot. Rich enough to pick the right one, compact enough
 * to fit in a manifest of dozens of candidates.
 */
export interface AttachableActionOption {
  id: string;
  name: string;
  displayName?: string;
  datasourceName?: string;
  actionType: EDatasourceType;
  returnTypeName: string;
  returnCardinality: ECardinality;
  parameters: AttachableActionParameter[];
}

export interface AttachableActionParameter {
  name: string;
  typeName: string;
  cardinality: ECardinality;
  required: boolean;
}

export interface AttachablePlatformContext {
  projectType: EProjectType;
  isWebScreen: boolean;
  dashboardState: DashboardState;
  isOfflineScreen: boolean;
}

export interface AttachableActionsInput {
  actionDefinitions: IActionDefinitionDto[];
  platform: AttachablePlatformContext;
}

/**
 * Platform-only compatibility check. Decides whether an action definition can
 * run on the current project type / screen type / runtime context, mirroring
 * the filter the designer symbol picker engine has carried for years.
 *
 * Shared with `DesignerSymbolPickerEngine.getProjectCompatibleActionDefinitions`
 * so both callers stay in sync. Kept pure so it can be unit-tested and invoked
 * from non-React code paths.
 */
export const isActionDefinitionPlatformCompatible = (
  def: IActionDefinitionDto,
  platform: AttachablePlatformContext
): boolean => {
  const { projectType, isWebScreen, dashboardState, isOfflineScreen } = platform;

  // Order matches the engine's historical filter exactly: app-support rejections
  // run BEFORE the offline override so Web-only JS functions on a Mobile offline
  // screen stay rejected rather than slipping through the JS-funcs carve-out.
  if (
    def.appSupportType === EAppSupportType.Web &&
    projectType !== EProjectType.Desktop &&
    dashboardState !== DashboardState.scheduledJobs &&
    !isWebScreen
  ) {
    return false;
  }

  if (
    def.appSupportType === EAppSupportType.Mobile &&
    projectType === EProjectType.Desktop &&
    dashboardState !== DashboardState.scheduledJobs
  ) {
    return false;
  }

  if (isOfflineScreen) {
    return isOfflineCompatibleActionDefinition(def);
  }

  return true;
};

/**
 * Full compatibility check for the bindables flow — platform check plus a
 * deprecated-action guard so `ui_bind.get_options` never offers the LLM
 * actions the catalog has retired. The engine's own action-result walker
 * deliberately does NOT apply the deprecated guard: it needs to keep showing
 * already-placed deprecated actions in the user's cascader.
 */
export const isActionDefinitionCompatible = (
  def: IActionDefinitionDto,
  platform: AttachablePlatformContext
): boolean => {
  if (def.isDeprecated === true) {
    return false;
  }
  return isActionDefinitionPlatformCompatible(def, platform);
};

const mapParameter = (param: IActionParameter): AttachableActionParameter => ({
  name: param.name,
  typeName: param.Typename ?? "String",
  cardinality: param.cardinality ?? ECardinality.One,
  required: param.isOptionalParameter !== true
});

/**
 * Return every action definition in the project the LLM is allowed to attach
 * to an event, compressed to a minimal decision-ready shape. This fixes the
 * chicken-and-egg where the cascader only surfaces already-placed instances;
 * events need to see the catalog, because attaching *is* instantiation.
 */
export const getAttachableActionDefinitions = (input: AttachableActionsInput): AttachableActionOption[] => {
  const { actionDefinitions, platform } = input;
  const result: AttachableActionOption[] = [];
  for (const def of actionDefinitions) {
    if (!isActionDefinitionCompatible(def, platform)) {
      continue;
    }
    result.push({
      id: def.id,
      name: def.name,
      displayName: def.displayName,
      datasourceName: def.kDatasource_Name,
      actionType: def.actionType,
      returnTypeName: def.returnTypeName,
      returnCardinality: def.returnCardinality,
      parameters: (def.parameters ?? []).map(mapParameter)
    });
  }
  return result;
};
